import axiosInstance from "utils/axios";
import { toast } from "sonner";
import { getCompanyIdFromToken } from "utils/tokenUtils";
import { uploadAttachments, DocumentUploadFeature } from "services/documentUploadService";

// Get all users
export const getAllUsers = async (params = {}) => {
  try {
    const requestBody = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      search: params.search || "",
      sortColumn: params.sortColumn || "FullName",
      sortOrder: params.sortOrder || "ASC",
      userType: 1,
      filterCompanyIds: params.filterCompanyIds || (params.companyId ? [params.companyId] : null),
      companyId: params.companyId || null,
      CompanyId: params.companyId || null,
      designationIds: (params.designationIds && params.designationIds.length > 0) ? params.designationIds : null,
      isInternal: params.isInternal !== undefined ? params.isInternal : null,
    };

    const response = await axiosInstance.post(
      "/api/User/GetAllUsers",
      requestBody,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data to match the table format
      const transformedUsers = userData.map((user) => ({
        ...user,
        id: user.UserId,
        userId: user.UserId,
        name: user.FullName,
        email: user.Email,
        // No "N/A" placeholder here — this object is reused as EditUserModal's data source, and a
        // fake string like "N/A" gets picked up as a real phone number there (normalizePhone then
        // prepends "+61", producing "+61N/A"). Leave it empty; the table cell renders that as blank.
        phone: user.PhoneNumber || "",
        role: user.RoleName,
        roleId: user.RoleId,
        userType: user.UserType,
        status: user.IsActive,
        isActive: user.IsActive,
        createdOn: user.CreatedOn,
        companyId: user.ExternalCompanyId || user.CompanyId,
        externalCompanyId: user.ExternalCompanyId,
        ExternalCompanyId: user.ExternalCompanyId,
        companyName:
          user.Companyname || user.companyname || user.CompanyName || "N/A",
        emailVerified: user.EmailVerified,
        phoneVerified: user.PhoneVerified,
        reportTo: user.ReportTo,
        // Add these fields for edit modal and view modal
        DesignationId: user.DesignationId,
        DesignationName: user.DesignationName,
        DesignationIds: (user.Designations || []).map((d) => String(d.DesignationId)),
        Designations: user.Designations || [],
        ReportTo: user.ReportTo,
        ReportToName: user.ReportToName,
        RoleId: user.RoleId,
        RoleName: user.RoleName,
        IsCounsellor: user.IsCounsellor,
        IsActive: user.IsActive,
        FullName: user.FullName,
        Email: user.Email,
        PhoneNumber: user.PhoneNumber,
        CreatedOn: user.CreatedOn,
        avatar: user.ProfileImageUrl || null,
        externalCompanyId: user.ExternalCompanyId,
        IsInternalRole: user.IsInternalRole,
        ContactDetail: user.ContactDetail || [],
        // Internal-user profile fields (Settings > Internal Users) — EditUserModal's prefill.
        dateOfBirth: user.DateOfBirth,
        personalEmail: user.PersonalEmail,
        nextOfKin: user.NextOfKin,
        emergencyContactNumber: user.EmergencyContactNumber,
        visaStatusId: user.VisaStatusId,
        visaStatus: user.VisaStatus,
        visaExpiryDate: user.VisaExpiryDate,
        whiteCardNumber: user.WhiteCardNumber,
        whiteCardExpiryDate: user.WhiteCardExpiryDate,
        accessCardNumber: user.AccessCardNumber,
        accessCardExpiryDate: user.AccessCardExpiryDate,
        silicaCardNumber: user.SilicaCardNumber,
        silicaCardExpiryDate: user.SilicaCardExpiryDate,
        // Uploaded documents for the 3 fixed WHS cards — one flat list, each entry tagged with
        // DocumentCategory ('WhiteCard' | 'AccessCard' | 'SilicaCard'); EditUserModal splits it
        // per card. Includes who uploaded it and when, alongside size, for display.
        cardDocuments: user.CardDocuments || [],
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to fetch users";

    toast.error("Failed to load users", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Helper function to get role name from UserType
function getRoleNameFromType(userType) {
  const roleMap = {
    0: "User",
    1: "User",
    2: "Admin",
    3: "Super Admin",
  };
  return roleMap[userType] || "Unknown";
}

// Create a new user
export const createUser = async (userData) => {
  try {
    // Ensure the data matches the new API structure
    const requestBody = {
      fullName: userData.fullName,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      designationIds: userData.designationIds || [],
      roleId: userData.roleId,
      reportTo: userData.reportTo || null,
      isCounsellor: userData.isCounsellor || false,
      companyId: getCompanyIdFromToken() || null,
      externalCompanyId: userData.companyId || null,
      IsInternalRole: userData.IsInternalRole,
      emails: userData.emails || [],
      phoneNumbers: userData.phoneNumbers || [],
      dateOfBirth: userData.dateOfBirth || null,
      personalEmail: userData.personalEmail || null,
      nextOfKin: userData.nextOfKin || null,
      emergencyContactNumber: userData.emergencyContactNumber || null,
      visaStatusId: userData.visaStatusId || null,
      visaStatus: userData.visaStatus || null,
      visaExpiryDate: userData.visaExpiryDate || null,
    };

    const response = await axiosInstance.post(
      "/api/User/CreateUser",
      requestBody,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User created successfully", {
        description: "The new user has been added to the system.",
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User created successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to create user");
    }
  } catch (error) {
    console.error("Error creating user:", error);

    // The axios interceptor normalizes the error, so error.message contains the backend message
    const errorMessage =
      error.message || error.response?.data?.message || "Failed to create user";

    toast.error("Failed to create user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Update user
export const updateUser = async (userId, userData) => {
  try {
    const response = await axiosInstance.put(
      `/api/User/UpdateUser/${userId}`,
      userData,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User updated successfully");
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update user");
    }
  } catch (error) {
    console.error("Error updating user:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to update user";

    toast.error("Failed to update user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Takes a WHS card's `files` array (a mix of raw File objects the user just picked via
// AttachmentsActions, and already-saved document objects prefilled from GetAllUsers' CardDocuments),
// uploads any new File objects through the generic Initiate/Confirm pipeline, and returns the FULL
// documents array ready for upsertUserCard's `documents` param. Returns undefined when the card has
// no files at all, so callers can omit the param rather than sending an empty array.
export const buildCardDocumentsPayload = async (files) => {
  if (!files || files.length === 0) return undefined;

  const newFiles = files.filter((f) => f instanceof File);
  const existingDocs = files
    .filter((f) => !(f instanceof File))
    .map((f) => ({
      dataDocumentId: f.dataDocumentId || f.DataDocumentId || null,
      originalFilename: f.originalFilename || f.OriginalFilename || f.name || "document",
      storedFilename: f.storedFilename || f.StoredFilename || f.FileUrl || f.filePath || "",
      fileURL: f.fileURL || f.FileUrl || f.filePath || f.FileURL || "",
      fileSize: f.fileSize || f.FileSize || f.size || 0,
    }));

  let newDocs = [];
  if (newFiles.length > 0) {
    const companyId = getCompanyIdFromToken();
    const uploadRes = await uploadAttachments(DocumentUploadFeature.USER_CARD_DOCUMENT, newFiles, companyId);
    if (!uploadRes.success) {
      throw new Error(uploadRes.message || "Failed to upload card document(s)");
    }
    newDocs = (uploadRes.data || []).map((att, idx) => ({
      dataDocumentId: null,
      originalFilename: att.fileName || newFiles[idx]?.name || "document",
      storedFilename: att.filePath || att.fileKey || "",
      fileURL: att.filePath || att.fileKey || "",
      fileSize: newFiles[idx]?.size || 0,
    }));
  }

  return [...existingDocs, ...newDocs];
};

// Upsert one of the 3 fixed WHS cards (White/Access/Silica) on an internal user's profile.
// Second phase, called after createUser/updateUserDetails return the user's ID. Skips the call
// entirely when there's nothing at all to save for that card.
//
// `documents`, when passed, is the FULL desired set of documents for this card (already-uploaded
// ones the caller wants to keep, plus newly-uploaded ones) — each shaped like
// { dataDocumentId, originalFilename, storedFilename, fileURL, fileSize }. Omitting it (undefined)
// leaves existing documents untouched server-side only in the sense of not being sent — the
// backend otherwise treats "not sent" as "delete all", so always pass the full set once any
// document exists for this card, never just the newly-added ones.
export const upsertUserCard = async (userId, cardType, cardNumber, expiryDate, documents) => {
  if (!userId || !cardType) return { success: true, skipped: true };
  if (!cardNumber && !expiryDate && (!documents || documents.length === 0)) {
    return { success: true, skipped: true };
  }

  try {
    const response = await axiosInstance.post("/api/User/UpsertUserCard", {
      userId,
      cardType,
      cardNumber: cardNumber || null,
      expiryDate: expiryDate || null,
      ...(documents !== undefined ? { documents } : {}),
    });

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      return { success: true, data: response.data.data };
    }
    throw new Error(response.data.message || `Failed to save ${cardType}`);
  } catch (error) {
    console.error(`Error saving ${cardType}:`, error);
    const errorMessage =
      error.response?.data?.message || error.message || `Failed to save ${cardType}`;
    toast.error(`Failed to save ${cardType}`, { description: errorMessage });
    return { success: false, error: errorMessage };
  }
};

// Delete user
export const deleteUser = async (userId, transfers = {}) => {
  try {
    const requestBody = {
      userId: userId,
      ...transfers,
    };
    const response = await axiosInstance.post(
      `/api/User/DeleteUser`,
      requestBody,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User deleted successfully");
      return {
        success: true,
        message: response.data.message || "User deleted successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to delete user");
    }
  } catch (error) {
    console.error("Error deleting user:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to delete user";

    toast.error("Failed to delete user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Get user responsibilities before deletion
export const getUserResponsibilities = async (userId) => {
  try {
    const response = await axiosInstance.get(
      `/api/User/GetUserResponsibilities?userId=${userId}`,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      let responsibilitiesData = response.data.data;
      if (typeof responsibilitiesData === "string") {
        try {
          responsibilitiesData = JSON.parse(responsibilitiesData);
        } catch (parseError) {
          console.error("Error parsing responsibilities data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      return {
        success: true,
        data: responsibilitiesData,
        message:
          response.data.message || "Responsibilities fetched successfully",
      };
    } else {
      throw new Error(
        response.data.message || "Failed to fetch responsibilities",
      );
    }
  } catch (error) {
    console.error("Error fetching user responsibilities:", error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch user responsibilities";

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

// Toggle user status (activate/deactivate)
export const toggleUserStatus = async (userId, isActive) => {
  try {
    const response = await axiosInstance.patch(
      `/api/User/ToggleUserStatus/${userId}`,
      { isActive },
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success(
        `User ${isActive ? "activated" : "deactivated"} successfully`,
      );
      return {
        success: true,
        message:
          response.data.message ||
          `User ${isActive ? "activated" : "deactivated"} successfully`,
      };
    } else {
      throw new Error(response.data.message || "Failed to toggle user status");
    }
  } catch (error) {
    console.error("Error toggling user status:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to toggle user status";

    toast.error("Failed to update user status", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Update user details (sends data as query parameters)
export const updateUserDetails = async (userData) => {
  try {
    const formData = new FormData();

    formData.append("UserId", userData.userId);
    formData.append("FullName", userData.fullName || "");
    formData.append("PhoneNumber", userData.phoneNumber || "");

    // A caller that passes an actual array (EditUserModal always does, even when every email/phone
    // was removed and it's now []) means "this is the full desired list, including empty" — send it
    // as-is so the backend's EmailsJson/PhoneNumbersJson clear-sync runs. Previously this only sent
    // the array when non-empty, so removing the last email/phone silently sent nothing and the
    // backend's "field not present = leave unchanged" rule left the stale value in place. A caller
    // that omits the array entirely (e.g. RowActions' role-only update) still leaves it untouched.
    if (Array.isArray(userData.emails)) {
      formData.append("EmailsJson", JSON.stringify(userData.emails));
    } else if (userData.email) {
      formData.append("EmailsJson", JSON.stringify([userData.email]));
    }

    if (Array.isArray(userData.phoneNumbers)) {
      formData.append("PhoneNumbersJson", JSON.stringify(userData.phoneNumbers));
    } else if (userData.phoneNumber) {
      formData.append("PhoneNumbersJson", JSON.stringify([userData.phoneNumber]));
    }
    // Only send DesignationIds when the caller actually means to change them —
    // omitting the field entirely leaves the user's existing designations untouched.
    if (Array.isArray(userData.designationIds)) {
      userData.designationIds.forEach((id) => formData.append("DesignationIds", id));
    }
    formData.append("RoleId", userData.roleId || "");
    formData.append("ReportTo", userData.reportTo || "");
    formData.append("IsCounseller", userData.isCounsellor ? "true" : "false");
    formData.append("CompanyId", getCompanyIdFromToken() || "");
    if (userData.externalCompanyId) {
      formData.append("ExternalCompanyId", userData.externalCompanyId);
    }

    if (userData.dateOfBirth) formData.append("DateOfBirth", userData.dateOfBirth);
    if (userData.personalEmail) formData.append("PersonalEmail", userData.personalEmail);
    if (userData.nextOfKin) formData.append("NextOfKin", userData.nextOfKin);
    if (userData.emergencyContactNumber) formData.append("EmergencyContactNumber", userData.emergencyContactNumber);
    if (userData.visaStatusId) formData.append("VisaStatusId", userData.visaStatusId);
    if (userData.visaStatus) formData.append("VisaStatus", userData.visaStatus);
    if (userData.visaExpiryDate) formData.append("VisaExpiryDate", userData.visaExpiryDate);

    // Optional profile image
    if (userData.profileImage) {
      formData.append("ProfileImage", userData.profileImage);
    }

    const response = await axiosInstance.post(
      "/api/User/UpdateUserDetails",
      formData,
      {
        // Content-Type handled by axios interceptor for FormData
        timeout: 60000, // 1 minute timeout for profile image upload
      },
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User updated successfully", {
        description: "The user details have been updated.",
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update user");
    }
  } catch (error) {
    console.error("Error updating user:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to update user";

    toast.error("Failed to update user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Get users for assigning leads (counselors/users who can be assigned leads)
export const getUsersForAssignLeads = async () => {
  try {
    const response = await axiosInstance.get(
      "/api/User/GetUsersForAssignLeads",
      {
        showLoader: false,
      },
    );

    // Check if the response indicates failure (case-insensitive)
    if (
      response.data?.status === "Failure" ||
      response.data?.statusCode >= 400
    ) {
      throw new Error(
        response.data?.message || "Failed to fetch users for assignment",
      );
    }

    if (
      response.data.statusCode === 200 &&
      response.data.status?.toLowerCase() === "success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data
      const transformedUsers = userData.map((user) => ({
        id: user.UserId,
        userId: user.UserId,
        fullName: user.FullName,
        designationName: user.DesignationName,
        designations: user.Designations || [],
        roleName: user.RoleName,
        companyName: user.Companyname || user.CompanyName,
        // For dropdown labels
        value: user.UserId,
        label: user.FullName,
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users for lead assignment:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch users for assignment";

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Get internal users for assigning leads
export const getInternalUsersForAssigningLeads = async (companyId = null) => {
  try {
    const params = companyId ? { companyId } : {};
    const response = await axiosInstance.get(
      "/api/User/GetInternalUsersForAssigningLeads",
      {
        params,
        showLoader: false,
      },
    );

    // Check if the response indicates failure (case-insensitive)
    if (
      response.data?.status === "Failure" ||
      response.data?.statusCode >= 400
    ) {
      throw new Error(
        response.data?.message ||
          "Failed to fetch internal users for assignment",
      );
    }

    if (
      response.data.statusCode === 200 &&
      response.data.status?.toLowerCase() === "success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data
      const transformedUsers = userData.map((user) => ({
        id: user.UserId,
        userId: user.UserId,
        fullName: user.FullName,
        roleId: user.RoleId,
        roleName: user.RoleName,
        designationName: user.DesignationName,
        designations: user.Designations,
        isInternalRole: user.IsInternalRole,
        // For dropdown labels
        value: user.UserId,
        label: user.FullName,
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Internal users fetched successfully",
      };
    } else {
      throw new Error(
        response.data.message || "Failed to fetch internal users",
      );
    }
  } catch (error) {
    console.error("Error fetching internal users for lead assignment:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch internal users for assignment";

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Get sales consultant users
export const getSalesConsultantUsers = async () => {
  try {
    const response = await axiosInstance.get(
      "/api/User/GetSalesConsultantUsers",
      {
        showLoader: false,
      },
    );

    // Check if the response indicates failure (case-insensitive)
    if (
      response.data?.status === "Failure" ||
      response.data?.statusCode >= 400
    ) {
      throw new Error(
        response.data?.message ||
          "Failed to fetch sales consultant users",
      );
    }

    if (
      response.data.statusCode === 200 &&
      response.data.status?.toLowerCase() === "success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data
      const transformedUsers = userData.map((user) => ({
        id: user.UserId,
        userId: user.UserId,
        fullName: user.FullName,
        roleId: user.RoleId,
        roleName: user.RoleName,
        designationName: user.DesignationName,
        designations: user.Designations,
        isInternalRole: user.IsInternalRole,
        // For dropdown labels
        value: user.UserId,
        label: user.FullName,
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Sales consultant users fetched successfully",
      };
    } else {
      throw new Error(
        response.data.message || "Failed to fetch sales consultant users",
      );
    }
  } catch (error) {
    console.error("Error fetching sales consultant users:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch sales consultant users";

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Get all counselors (legacy - for backward compatibility, now uses getUsersForAssignLeads)
export const getAllCounselors = async () => {
  try {
    // Use the new API instead
    const result = await getUsersForAssignLeads();

    if (result.success) {
      // Add email field for backward compatibility (even if empty)
      const transformedCounselors = result.data.map((user) => ({
        ...user,
        email: user.email || "", // Add empty email if not provided
        phoneNumber: user.phoneNumber || "",
        designationId: user.designationId || null,
        roleId: user.roleId || null,
      }));

      return {
        success: true,
        data: transformedCounselors,
        totalRecords: result.totalRecords,
        message: result.message,
      };
    } else {
      throw new Error(result.error || "Failed to fetch counselors");
    }
  } catch (error) {
    console.error("Error fetching counselors:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch counselors";

    toast.error("Failed to load counselors", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

export const getAllUsersWithDesignations = async (companyId = null, hideWorkerBoys = false) => {
  try {
    let url = companyId
      ? `/api/User/GetAllUserWithDesignations?companyId=${companyId}`
      : "/api/User/GetAllUserWithDesignations";

    if (hideWorkerBoys) {
      url += url.includes("?") ? `&hideWorkerBoys=true` : `?hideWorkerBoys=true`;
    }

    const response = await axiosInstance.get(url);

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data
      const transformedUsers = userData.map((user) => ({
        userId: user.UserId,
        fullName: user.FullName,
        roleId: user.RoleId,
        roleName: user.RoleName,
        designationId: user.DesignationId,
        designationName: user.DesignationName,
        designationDescription: user.DesignationDescription,
        designationIds: (user.Designations || []).map((d) => String(d.DesignationId)),
        designations: user.Designations || [],
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users with designations:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to fetch users";

    toast.error("Failed to load users", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

export const getAllUsersForApplication = async (params = {}) => {
  try {
    const requestBody = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      search: params.search || "",
      sortColumn: params.sortColumn || "FullName",
      sortOrder: params.sortOrder || "ASC",
      userType: 2,
    };

    const response = await axiosInstance.post(
      "/api/User/GetAllUsers",
      requestBody,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data to match the table format
      const transformedUsers = userData.map((user) => ({
        id: user.UserId,
        userId: user.UserId,
        name: user.FullName,
        email: user.Email,
        // See getAllUsers() above — no "N/A" placeholder baked into the data, same reason.
        phone: user.PhoneNumber || "",
        role: user.RoleName,
        roleId: user.RoleId,
        userType: user.UserType,
        companyId: user.CompanyId,
        companyName:
          user.Companyname || user.companyname || user.CompanyName || "N/A",
        designationId: user.DesignationId || null,
        designationName: user.DesignationName || null,
        reportTo: user.ReportTo || null,
        reportToName: user.ReportToName || null,
        isAdmin: user.IsAdmin,
        isActive: user.IsActive,
        emailVerified: user.EmailVerified,
        phoneVerified: user.PhoneVerified,
        createdOn: user.CreatedOn,
        avatar: user.ProfileImageUrl || null, // In case API adds profile image later
        externalCompanyId: user.ExternalCompanyId,
        ContactDetail: user.ContactDetail || [],
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to fetch users";

    toast.error("Failed to load users", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

export const getAllActiveUsers = async () => {
  try {
    const response = await axiosInstance.get("/api/User/GetAllActiveUsers");
    if (
      response.data?.statusCode === 200 &&
      response.data?.status === "Success"
    ) {
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing active users:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }
      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }
      const transformed = userData.map((user) => ({
        id: user.UserId || user.id,
        userId: user.UserId || user.id,
        fullName: user.FullName || user.fullName || user.name,
        value: user.UserId || user.id,
        label: user.FullName || user.fullName || user.name,
        designationName: user.DesignationName || user.designationName,
        designations: user.Designations || user.designations,
        roleName: user.RoleName || user.roleName,
      }));
      return {
        success: true,
        data: transformed,
        message: response.data.message || "Active users fetched successfully",
      };
    } else {
      throw new Error(response.data?.message || "Failed to fetch active users");
    }
  } catch (error) {
    console.error("Error fetching active users:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch active users",
      data: [],
    };
  }
};

// Fetch user details by phone number and jobId for WHS QR check-in
export const fetchUserDetailsByPhoneNumber = async (phoneNumber, jobId) => {
  try {
    const response = await axiosInstance.get(
      `/api/User/FetchUserDetailsByPhoneNumber?phoneNumber=${encodeURIComponent(phoneNumber)}&jobId=${jobId}`
    );

    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let rawData = response.data.data;
      if (typeof rawData === "string" && rawData.trim()) {
        try {
          rawData = JSON.parse(rawData);
        } catch (e) {
          console.error("Error parsing FetchUserDetails response data JSON:", e);
        }
      }
      return {
        success: true,
        data: rawData,
        message: response.data.message || "User details fetched successfully",
      };
    } else {
      return {
        success: false,
        message: response.data.message || "Failed to fetch user details",
        data: null,
      };
    }
  } catch (error) {
    console.error("Error in fetchUserDetailsByPhoneNumber:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to fetch user details",
      data: null,
    };
  }
};

// Upsert user details with company and certifications for WHS QR check-in
export const upsertUserWithCertification = async (payload) => {
  try {
    const response = await axiosInstance.post(
      "/api/User/UpsertUserWithCertification",
      payload
    );

    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let rawData = response.data.data;
      if (typeof rawData === "string" && rawData.trim()) {
        try {
          rawData = JSON.parse(rawData);
        } catch (e) {
          console.error("Error parsing UpsertUserWithCertification response data JSON:", e);
        }
      }
      return {
        success: true,
        data: rawData,
        message: response.data.message || "User & certifications saved successfully",
      };
    } else {
      return {
        success: false,
        message: response.data.message || "Failed to save user details",
        data: null,
      };
    }
  } catch (error) {
    console.error("Error in upsertUserWithCertification:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to save user details",
      data: null,
    };
  }
};

// Upsert a single user certification
export const upsertUserCertification = async (payload) => {
  try {
    const response = await axiosInstance.post(
      "/api/User/UpsertUserCertification",
      payload
    );

    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let rawData = response.data.data;
      if (typeof rawData === "string" && rawData.trim()) {
        try {
          rawData = JSON.parse(rawData);
        } catch (e) {
          console.error("Error parsing UpsertUserCertification response data JSON:", e);
        }
      }
      return {
        success: true,
        data: rawData,
        message: response.data.message || "User certification saved successfully",
      };
    } else {
      return {
        success: false,
        message: response.data.message || "Failed to save user certification",
        data: null,
      };
    }
  } catch (error) {
    console.error("Error in upsertUserCertification:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to save user certification",
      data: null,
    };
  }
};

// Delete user certification (or a specific document)
export const deleteUserCertification = async ({ certificationDataId, dataDocumentId }) => {
  try {
    let url = "/api/User/DeleteUserCertification?";
    if (certificationDataId) {
      url += `certificationDataId=${encodeURIComponent(certificationDataId)}&`;
    }
    if (dataDocumentId) {
      url += `dataDocumentId=${encodeURIComponent(dataDocumentId)}&`;
    }
    url = url.endsWith("&") ? url.slice(0, -1) : url;

    const response = await axiosInstance.post(url);

    if (response.data.statusCode === 200 && response.data.status === "Success") {
      return {
        success: true,
        message: response.data.message || "Deleted successfully",
      };
    } else {
      return {
        success: false,
        message: response.data.message || "Failed to delete",
      };
    }
  } catch (error) {
    console.error("Error in deleteUserCertification:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to delete",
    };
  }
};
